import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Users, RefreshCw, Loader2, Search, Plus, Trash2, Mail, Instagram,
  Facebook, MessageCircle, UserPlus, Download, Tags, Flame,
} from "lucide-react";
import { toast } from "sonner";

const SOURCE_META = {
  gmail: { label: "Gmail", color: "bg-red-50 text-red-700 border-red-200", Icon: Mail },
  instagram: { label: "Instagram", color: "bg-pink-50 text-pink-700 border-pink-200", Icon: Instagram },
  facebook: { label: "Facebook", color: "bg-blue-50 text-blue-700 border-blue-200", Icon: Facebook },
  whatsapp: { label: "WhatsApp", color: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: MessageCircle },
  manual: { label: "Manual", color: "bg-stone-100 text-stone-700 border-stone-200", Icon: UserPlus },
};

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function toCsv(rows) {
  const headers = ["nombre", "identificador", "fuente", "telefono", "empresa_contacto", "fecha_sync"];
  const escape = (v) => {
    if (v == null) return "";
    const s = String(v).replace(/"/g, '""');
    return /[,"\n]/.test(s) ? `"${s}"` : s;
  };
  const lines = [headers.join(",")];
  for (const r of rows) lines.push(headers.map((h) => escape(r[h])).join(","));
  return lines.join("\n");
}

function downloadCsv(rows, filename = "clientes.csv") {
  const csv = toCsv(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function Customers() {
  const { isMaster } = useAuth();
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [fuente, setFuente] = useState("all");
  const [tag, setTag] = useState("all");
  const [sortHot, setSortHot] = useState(false);
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ nombre: "", identificador: "", fuente: "manual", telefono: "", empresa_contacto: "" });
  const [saving, setSaving] = useState(false);
  const [tags, setTags] = useState([]);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState("#00A36C");
  const [assignFor, setAssignFor] = useState(null); // contact being tagged

  const loadTags = async () => {
    try {
      const r = await api.get("/tags");
      setTags(r.data.items || []);
    } catch (_) { /* silent */ }
  };
  useEffect(() => { loadTags(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fuente !== "all") params.set("fuente", fuente);
      if (tag !== "all") params.set("tag", tag);
      if (sortHot) params.set("sort", "hot");
      if (q.trim()) params.set("q", q.trim());
      const r = await api.get(`/customers?${params.toString()}`);
      setItems(r.data.items || []);
      setTotals(r.data.total_by_source || {});
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar contactos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fuente, tag, sortHot]);

  const doSearch = (e) => { e?.preventDefault(); load(); };

  const doSyncGmail = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/customers/gmail/sync");
      if (r.data.ok) {
        toast.success(`Gmail sincronizado: +${r.data.created} nuevos · ${r.data.updated} actualizados · ${r.data.total} total`);
        await load();
      } else {
        toast.error(`No se pudo sincronizar: ${r.data.error}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  const doAdd = async () => {
    if (!form.nombre.trim() || !form.identificador.trim()) {
      toast.error("Nombre e identificador son obligatorios");
      return;
    }
    setSaving(true);
    try {
      await api.post("/customers", form);
      toast.success("Contacto guardado");
      setAddOpen(false);
      setForm({ nombre: "", identificador: "", fuente: "manual", telefono: "", empresa_contacto: "" });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (id) => {
    if (!window.confirm("¿Eliminar este contacto?")) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Contacto eliminado");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  // Fase 25 · Tags CRUD + assign
  const doCreateTag = async () => {
    if (!newTagName.trim()) { toast.error("Nombre requerido"); return; }
    try {
      await api.post("/tags", { name: newTagName.trim(), color: newTagColor });
      setNewTagName("");
      await loadTags();
      toast.success("Tag creado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };
  const doDeleteTag = async (t) => {
    if (!window.confirm(`¿Eliminar tag "${t.name}"?`)) return;
    try {
      await api.delete(`/tags/${t.id}`);
      await loadTags();
      await load();
      toast.success("Tag eliminado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };
  const toggleTagOnContact = async (contact, tagId) => {
    const current = new Set(contact.tags || []);
    if (current.has(tagId)) current.delete(tagId); else current.add(tagId);
    try {
      await api.put(`/tags/assign/${contact.id}`, { tag_ids: Array.from(current) });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const tagById = (id) => tags.find((t) => t.id === id);

  const summary = useMemo(() => ([
    { key: "gmail", n: totals.gmail || 0 },
    { key: "instagram", n: totals.instagram || 0 },
    { key: "facebook", n: totals.facebook || 0 },
    { key: "whatsapp", n: totals.whatsapp || 0 },
    { key: "manual", n: totals.manual || 0 },
  ]), [totals]);

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="customers-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="divider-gold mb-3" />
          <h1 className="font-serif-display text-4xl text-brand-ink font-semibold flex items-center gap-3">
            <span className="inline-flex h-10 w-10 rounded-lg items-center justify-center bg-brand-emerald text-white">
              <Users className="h-5 w-5" strokeWidth={1.8} />
            </span>
            Agenda FStudy
          </h1>
          <p className="text-sm text-stone-500 mt-2">
            Base unificada de contactos: Gmail, redes sociales y WhatsApp. Total: <span className="font-semibold text-brand-ink">{total}</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isMaster && (
            <Button
              onClick={doSyncGmail}
              disabled={syncing}
              data-testid="btn-sync-gmail"
              className="bg-brand-gold text-brand-ink hover:bg-brand-gold-dark hover:text-white shadow-gold"
            >
              {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Sincronizar Gmail
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setTagsOpen(true)}
            data-testid="btn-manage-tags"
            className="border-brand-emerald text-brand-emerald-dark hover:bg-brand-emerald-soft"
          >
            <Tags className="h-4 w-4 mr-2" /> Tags
          </Button>
          <Button
            variant={sortHot ? "default" : "outline"}
            onClick={() => setSortHot(!sortHot)}
            data-testid="btn-sort-hot"
            className={sortHot ? "bg-destructive text-white hover:bg-destructive/90" : ""}
          >
            <Flame className="h-4 w-4 mr-2" /> {sortHot ? "Leads calientes" : "Ordenar por score"}
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadCsv(items)}
            disabled={items.length === 0}
            data-testid="btn-export-csv"
          >
            <Download className="h-4 w-4 mr-2" /> Exportar CSV
          </Button>
          <Button
            onClick={() => setAddOpen(true)}
            variant="outline"
            data-testid="btn-add-contact"
            className="border-brand-emerald text-brand-emerald-dark hover:bg-brand-emerald-soft"
          >
            <Plus className="h-4 w-4 mr-2" /> Nuevo contacto
          </Button>
        </div>
      </div>

      {/* Summary por fuente */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3" data-testid="source-summary">
        {summary.map(({ key, n }) => {
          const meta = SOURCE_META[key];
          const Icon = meta.Icon;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setFuente(fuente === key ? "all" : key)}
              className={[
                "card-elevated p-4 flex items-center gap-3 text-left transition-all",
                fuente === key ? "ring-2 ring-brand-gold border-brand-gold" : "hover:border-brand-emerald/40",
              ].join(" ")}
              data-testid={`filter-source-${key}`}
            >
              <span className={`inline-flex h-9 w-9 rounded-md items-center justify-center border ${meta.color}`}>
                <Icon className="h-4 w-4" strokeWidth={1.8} />
              </span>
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">{meta.label}</div>
                <div className="font-serif-display text-2xl font-semibold text-brand-ink leading-none mt-1">{n}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Filtros */}
      <div className="card-elevated p-4">
        <form className="flex flex-col md:flex-row gap-3 items-stretch md:items-end" onSubmit={doSearch}>
          <div className="flex-1 space-y-1.5">
            <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">Buscar</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Nombre, email, teléfono…"
                className="pl-8 bg-white"
                data-testid="input-search"
              />
            </div>
          </div>
          <div className="w-full md:w-56 space-y-1.5">
            <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">Fuente</Label>
            <Select value={fuente} onValueChange={setFuente}>
              <SelectTrigger data-testid="select-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {Object.entries(SOURCE_META).map(([k, m]) => (
                  <SelectItem key={k} value={k}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-full md:w-56 space-y-1.5">
            <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">Tag</Label>
            <Select value={tag} onValueChange={setTag}>
              <SelectTrigger data-testid="select-tag"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {tags.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name} ({t.contact_count})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" data-testid="btn-search" className="bg-brand-emerald text-white hover:bg-brand-emerald-dark">
            <Search className="h-4 w-4 mr-2" /> Buscar
          </Button>
        </form>
      </div>

      {/* Tabla */}
      <div className="card-elevated overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-stone-500" data-testid="empty-state">
            {fuente === "all" && !q
              ? "Sin contactos aún. Sincronice Gmail o espere a que lleguen mensajes por WhatsApp/redes sociales."
              : "No hay resultados para este filtro."}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Identificador</TableHead>
                <TableHead>Fuente</TableHead>
                <TableHead className="hidden md:table-cell">Score</TableHead>
                <TableHead className="hidden md:table-cell">Tags</TableHead>
                <TableHead className="hidden lg:table-cell">Última sincronización</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => {
                const meta = SOURCE_META[c.fuente] || SOURCE_META.manual;
                const Icon = meta.Icon;
                const temp = c.temperature || "frio";
                const tempColor = temp === "caliente" ? "bg-destructive/10 text-destructive border-destructive/30"
                  : temp === "tibio" ? "bg-brand-gold-soft text-brand-gold-dark border-brand-gold/40"
                  : "bg-stone-100 text-stone-600 border-stone-200";
                return (
                  <TableRow key={c.id} data-testid={`row-${c.id}`}>
                    <TableCell className="font-medium text-brand-ink">{c.nombre}</TableCell>
                    <TableCell className="font-mono text-xs text-stone-600">{c.identificador}</TableCell>
                    <TableCell>
                      <Badge className={`border font-semibold ${meta.color}`}>
                        <Icon className="h-3 w-3 mr-1" strokeWidth={2} />
                        {meta.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell" data-testid={`score-${c.id}`}>
                      <div className="flex flex-wrap items-center gap-1">
                        <Badge className={`border ${tempColor}`} data-testid={`score-value-${c.id}`}>
                          <Flame className="h-3 w-3 mr-1" /> {c.interaction_score || 0}
                        </Badge>
                        <span
                          className={`text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border ${tempColor}`}
                          data-testid={`temp-chip-${c.id}`}
                          title={`Temperatura: ${temp}`}
                        >
                          {temp === "caliente" ? "🔥 caliente" : temp === "tibio" ? "☕ tibio" : "❄️ frío"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex flex-wrap gap-1 max-w-[180px]">
                        {(c.tags || []).map((tid) => {
                          const t = tagById(tid);
                          if (!t) return null;
                          return (
                            <span key={tid} className="text-[10px] rounded-full px-2 py-0.5 border" style={{ borderColor: t.color, color: t.color }}>
                              {t.name}
                            </span>
                          );
                        })}
                        <Button size="sm" variant="ghost" className="h-6 px-1 text-stone-500" onClick={() => setAssignFor(c)} data-testid={`btn-tags-${c.id}`}>
                          <Tags className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-xs text-stone-500">{fmt(c.fecha_sync || c.updated_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => doDelete(c.id)}
                        data-testid={`btn-delete-${c.id}`}
                        className="text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Fase 25 · Gestión de Tags */}
      <Dialog open={tagsOpen} onOpenChange={setTagsOpen}>
        <DialogContent className="max-w-lg" data-testid="tags-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Gestionar tags</DialogTitle>
            <DialogDescription>Tags por empresa. Los seed (activo, prospecto, lead_frio) son editables.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
              <div className="flex-1 space-y-1"><Label>Nuevo tag</Label><Input value={newTagName} onChange={(e) => setNewTagName(e.target.value)} placeholder="ej. cliente_premium" data-testid="input-new-tag" /></div>
              <div className="space-y-1"><Label>Color</Label><input type="color" value={newTagColor} onChange={(e) => setNewTagColor(e.target.value)} className="h-10 w-14 rounded border" /></div>
              <Button onClick={doCreateTag} data-testid="btn-create-tag" className="bg-brand-emerald text-white hover:bg-brand-emerald-dark"><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="space-y-1 max-h-60 overflow-y-auto">
              {tags.map((t) => (
                <div key={t.id} className="flex items-center gap-2 p-2 rounded border border-stone-200">
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: t.color }} />
                  <span className="text-sm text-brand-ink flex-1">{t.name}</span>
                  <span className="text-xs text-stone-500">{t.contact_count} contactos</span>
                  {!t.seed && (
                    <Button size="sm" variant="ghost" onClick={() => doDeleteTag(t)} className="text-destructive"><Trash2 className="h-3 w-3" /></Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fase 25 · Asignar tags a un contacto */}
      <Dialog open={!!assignFor} onOpenChange={(v) => !v && setAssignFor(null)}>
        <DialogContent className="max-w-lg" data-testid="assign-tags-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-xl">Tags de {assignFor?.nombre}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap gap-2">
            {tags.map((t) => {
              const active = (assignFor?.tags || []).includes(t.id);
              return (
                <button
                  key={t.id}
                  onClick={() => toggleTagOnContact(assignFor, t.id)}
                  className={`px-3 py-1.5 rounded-full text-sm border ${active ? "text-white" : "bg-white text-stone-700"}`}
                  style={active ? { backgroundColor: t.color, borderColor: t.color } : { borderColor: t.color, color: t.color }}
                  data-testid={`toggle-tag-${t.id}`}
                >
                  {t.name}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg" data-testid="add-contact-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Nuevo contacto</DialogTitle>
            <DialogDescription>
              Registra un contacto manual. También puedes usar Sincronizar Gmail o esperar a que lleguen mensajes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Nombre</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="add-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Identificador (email o teléfono)</Label>
              <Input value={form.identificador} onChange={(e) => setForm({ ...form, identificador: e.target.value })} data-testid="add-ident" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Fuente</Label>
                <Select value={form.fuente} onValueChange={(v) => setForm({ ...form, fuente: v })}>
                  <SelectTrigger data-testid="add-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(SOURCE_META).map(([k, m]) => (
                      <SelectItem key={k} value={k}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Teléfono (opcional)</Label>
                <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Empresa del contacto (opcional)</Label>
              <Input value={form.empresa_contacto} onChange={(e) => setForm({ ...form, empresa_contacto: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancelar</Button>
            <Button onClick={doAdd} disabled={saving} data-testid="btn-save-contact" className="bg-brand-emerald text-white hover:bg-brand-emerald-dark">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
